import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Form,
  Input,
  InputNumber,
  Progress,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
  message,
  Modal,
} from 'antd';
import {
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  HighlightOutlined,
  KeyOutlined,
  LoadingOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Paragraph, Text } = Typography;

const TOKEN_GUIDE_URL = 'https://lininn.cn/page/how-to-get-ai-token';
const PROVIDER_META = [
  { key: 'modelscope', label: 'ModelScope', placeholder: '粘贴你的 ModelScope Token' },
  { key: 'agnes', label: 'Agnes', placeholder: '粘贴你的 Agnes Token' },
];

const RATIO_PRESETS = [
  { value: '1:1', label: '1:1 方图', width: 1, height: 1 },
  { value: '16:9', label: '16:9 横屏', width: 16, height: 9 },
  { value: '9:16', label: '9:16 竖屏', width: 9, height: 16 },
  { value: '4:3', label: '4:3 横图', width: 4, height: 3 },
  { value: '3:4', label: '3:4 竖图', width: 3, height: 4 },
  { value: '3:2', label: '3:2 摄影横图', width: 3, height: 2 },
  { value: '2:3', label: '2:3 摄影竖图', width: 2, height: 3 },
  { value: '21:9', label: '21:9 超宽银幕', width: 21, height: 9 },
];

const RESOLUTION_PRESETS = [
  { value: '1k', label: '1K', longEdge: 1024 },
  { value: '2k', label: '2K', longEdge: 2048 },
  { value: '4k', label: '4K', longEdge: 3840 },
];

const DEFAULT_VIDEO_DURATION_SECONDS = 5;
const DEFAULT_VIDEO_FRAME_RATE = 24;
const MAX_VIDEO_FRAMES = 441;
const MIN_VIDEO_FRAME_RATE = 1;
const MAX_VIDEO_FRAME_RATE = 60;
const MAX_VIDEO_FRAMES_OPTION = 'max_frames';
const DEFAULT_VIDEO_WIDTH = 3840;
const DEFAULT_VIDEO_HEIGHT = 2160;
const RECOMMENDED_24FPS_VIDEO_FRAMES = {
  3: 81,
  5: 121,
  10: 241,
  15: 361,
  18: 441,
};
const VIDEO_DURATION_OPTIONS = [
  { value: 5, label: '约 5 秒' },
  { value: 10, label: '约 10 秒' },
  { value: 15, label: '约 15 秒' },
  { value: MAX_VIDEO_FRAMES_OPTION, label: '最长帧数（按分辨率）' },
];

// ── agnes-video-2.5 族（新参数接口）── 上游对非法值直接 400，前端先卡一道白名单。
const VIDEO_MODEL_25_RE = /video-2\.5/i;
const VIDEO_MODEL_FLASH_RE = /video-2\.5-flash/i;
const VIDEO25_RATIO_WHITELIST = ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'];
const VIDEO25_SIZE_OPTIONS = [
  { value: '720P', label: '720P' },
  { value: '960P', label: '960P' },
  { value: '2K', label: '2K' },
];
const VIDEO25_FLASH_SIZE_OPTIONS = [{ value: '720P', label: '720P（固定）' }];
const VIDEO25_DURATION_OPTIONS = [4, 6, 8, 10, 12].map((v) => ({ value: v, label: `约 ${v} 秒` }));
const VIDEO25_MAX_REFERENCE_IMAGES = 5;

function maxVideoFramesForSize(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return MAX_VIDEO_FRAMES;
  if (w >= 1920 || h >= 1080) return 169;
  if (w >= 1280 || h >= 704) return 409;
  return 961;
}

function calculateVideoFrames(durationSeconds, frameRate, width, height) {
  const maxFrames = maxVideoFramesForSize(width, height);
  if (durationSeconds === MAX_VIDEO_FRAMES_OPTION) {
    return maxFrames;
  }
  const seconds = Number(durationSeconds);
  const fps = Number(frameRate);
  if (!Number.isFinite(seconds) || !Number.isFinite(fps) || seconds <= 0 || fps <= 0) {
    return null;
  }
  if (fps === DEFAULT_VIDEO_FRAME_RATE && RECOMMENDED_24FPS_VIDEO_FRAMES[seconds]) {
    return Math.min(maxFrames, RECOMMENDED_24FPS_VIDEO_FRAMES[seconds]);
  }
  const normalizedFrames = Math.ceil((seconds * fps - 1) / 8) * 8 + 1;
  return Math.min(maxFrames, Math.max(1, normalizedFrames));
}

function getVideoTiming(values = {}) {
  const durationValue = values.durationSeconds || DEFAULT_VIDEO_DURATION_SECONDS;
  const frameRate = Number(values.frameRate || DEFAULT_VIDEO_FRAME_RATE);
  const width = values.width || DEFAULT_VIDEO_WIDTH;
  const height = values.height || DEFAULT_VIDEO_HEIGHT;
  const maxFrames = maxVideoFramesForSize(width, height);
  const numFrames = calculateVideoFrames(durationValue, frameRate, width, height);
  const durationSeconds = durationValue === MAX_VIDEO_FRAMES_OPTION && numFrames && frameRate
    ? numFrames / frameRate
    : Number(durationValue);
  return {
    durationSeconds,
    frameRate,
    maxFrames,
    numFrames,
    actualSeconds: numFrames && frameRate ? (numFrames / frameRate).toFixed(2) : null,
    isCapped: durationValue === MAX_VIDEO_FRAMES_OPTION || (Number.isFinite(durationSeconds * frameRate) && durationSeconds * frameRate > maxFrames),
  };
}

// 可视化比例选择器：每个比例渲染一个等比小方框，选中态走渐变描边。
function RatioSelect({ value, onChange, presets = RATIO_PRESETS }) {
  return (
    <div className="mg-ratio-grid">
      {presets.map((preset) => {
        const scale = 18 / Math.max(preset.width, preset.height);
        return (
          <button
            key={preset.value}
            type="button"
            className={`mg-ratio-chip${value === preset.value ? ' active' : ''}`}
            onClick={() => onChange && onChange(preset.value)}
          >
            <span
              className="mg-ratio-box"
              style={{
                width: Math.max(6, Math.round(preset.width * scale)),
                height: Math.max(6, Math.round(preset.height * scale)),
              }}
            />
            <span className="mg-ratio-label">{preset.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function imageSrc(image) {
  if (image.url) return image.url;
  if (image.b64_json) return `data:${image.mimeType || 'image/png'};base64,${image.b64_json}`;
  return '';
}

function sendDownloadToAndroid(url, defaultType) {
  if (typeof window === 'undefined') return false;

  const bridge = window.AndroidDownloadBridge;
  if (!bridge || typeof bridge.downloadLongPressUrl !== 'function') {
    return false;
  }

  const lowerUrl = String(url || '').toLowerCase();
  if (
    !lowerUrl.startsWith('http://') &&
    !lowerUrl.startsWith('https://') &&
    !lowerUrl.startsWith('data:')
  ) {
    return false;
  }

  const fallbackMime = defaultType === 'image' ? 'image/*' : 'video/*';
  bridge.downloadLongPressUrl(url, fallbackMime);
  return true;
}

function videoErrorMessage(source) {
  const error = source?.error || source?.parameters?.lastError;
  if (!error) return null;
  if (typeof error === 'string') return error;
  return error.message || error.code || null;
}

function MediaGenerator(props, ref) {
  const [imageForm] = Form.useForm();
  const [videoForm] = Form.useForm();
  const [configs, setConfigs] = useState([]);
  const [imageIds, setImageIds] = useState([]);
  const [videoImageIds, setVideoImageIds] = useState([]);
  const [imageUploads, setImageUploads] = useState([]);
  const [videoUploads, setVideoUploads] = useState([]);
  const [imageFileList, setImageFileList] = useState([]);
  const [videoFileList, setVideoFileList] = useState([]);
  const [imageResult, setImageResult] = useState(null);
  const [videoTask, setVideoTask] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [imageSubmitting, setImageSubmitting] = useState(false);
  const [videoSubmitting, setVideoSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [refreshingRecordId, setRefreshingRecordId] = useState(null);
  const [videoTiming, setVideoTiming] = useState(getVideoTiming());
  const [imagePreview, setImagePreview] = useState(null);
  const imagePreviewHistoryRef = useRef(false);
  const [activeMediaTab, setActiveMediaTab] = useState('image');
  const [quota, setQuota] = useState({ used: 0, limit: 0, remaining: 0 });
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [providerTokens, setProviderTokens] = useState([]);
  const [tokenInputs, setTokenInputs] = useState({ agnes: '', modelscope: '' });
  const [tokenSaving, setTokenSaving] = useState('');

  useEffect(() => {
    loadConfigs();
    // 进入页面即拉取历史，并对未完成的异步任务主动查询上游、回填状态。
    loadRecords().then((list) => {
      autoRefreshPendingVideos(list);
      autoRefreshPendingImages(list);
    });
    loadQuota();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      loadConfigs();
      loadRecords().then((list) => {
        autoRefreshPendingVideos(list);
        autoRefreshPendingImages(list);
      });
      loadQuota();
    }
  }));

  useEffect(() => {
    const handlePopState = () => {
      if (!imagePreviewHistoryRef.current) return;
      imagePreviewHistoryRef.current = false;
      setImagePreview(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await axios.get('/api/ai-media/configs', { withCredentials: true });
      setConfigs(response.data || []);
    } catch (err) {
      // AI 对话主功能不能被媒体配置加载失败阻断。
      console.error('Failed to load AI media configs:', err);
    }
  };

  const loadRecords = async () => {
    setRecordsLoading(true);
    try {
      const response = await axios.get('/api/ai-media/records', { withCredentials: true });
      const list = response.data || [];
      setRecords(list);
      return list;
    } catch (err) {
      message.error(err.response?.data?.error || '加载生成历史失败');
      return [];
    } finally {
      setRecordsLoading(false);
    }
  };

  // 视频未完成状态：queued/pending/processing/running 等，且尚无结果地址。
  const PENDING_VIDEO_STATUSES = ['queued', 'pending', 'processing', 'running', 'in_progress'];
  const isPendingVideoRecord = (record) =>
    record?.type === 'video' &&
    record.taskId &&
    record.configId &&
    !record.resultUrl &&
    PENDING_VIDEO_STATUSES.includes(String(record.status || '').toLowerCase());

  // 静默批量向上游查询未完成的视频任务并回填状态（无 toast，不覆盖当前 videoTask）。
  const autoRefreshingRef = useRef(false);
  const autoRefreshPendingVideos = async (recordList) => {
    if (autoRefreshingRef.current) return;
    const pending = (recordList || []).filter(isPendingVideoRecord);
    if (pending.length === 0) return;
    autoRefreshingRef.current = true;
    try {
      await Promise.allSettled(
        pending.map((record) =>
          axios.get(`/api/ai-media/videos/${record.taskId}`, {
            params: { configId: record.configId },
            withCredentials: true,
          })
        )
      );
      // 上游回写 DB 后重新拉列表刷新界面。
      await loadRecords();
    } catch (err) {
      // 自动刷新为后台行为，失败静默处理，不打扰用户。
      console.error('Auto refresh pending videos failed:', err);
    } finally {
      autoRefreshingRef.current = false;
    }
  };

  // 切换媒体子 Tab：切到「视频生成」时主动刷新未完成任务，让 queued 自愈。
  const handleMediaTabChange = (key) => {
    setActiveMediaTab(key);
    if (key === 'video') {
      loadRecords().then((list) => autoRefreshPendingVideos(list));
    } else if (key === 'image') {
      loadRecords().then((list) => autoRefreshPendingImages(list));
    }
  };

  // 图像未完成状态：queued/pending/processing/running 等，且尚无结果地址（异步渠道才有 taskId）。
  const PENDING_IMAGE_STATUSES = ['queued', 'pending', 'processing', 'running', 'in_progress'];
  const isPendingImageRecord = (record) =>
    record?.type === 'image' &&
    record.taskId &&
    record.configId &&
    !record.resultUrl &&
    PENDING_IMAGE_STATUSES.includes(String(record.status || '').toLowerCase());

  // 静默批量向上游查询未完成的图像任务并回填状态（无 toast）。
  const autoRefreshingImagesRef = useRef(false);
  const autoRefreshPendingImages = async (recordList) => {
    if (autoRefreshingImagesRef.current) return;
    const pending = (recordList || []).filter(isPendingImageRecord);
    if (pending.length === 0) return;
    autoRefreshingImagesRef.current = true;
    try {
      await Promise.allSettled(
        pending.map((record) =>
          axios.get(`/api/ai-media/images/${record.taskId}`, {
            params: { configId: record.configId },
            withCredentials: true,
          })
        )
      );
      await loadRecords();
    } catch (err) {
      console.error('Auto refresh pending images failed:', err);
    } finally {
      autoRefreshingImagesRef.current = false;
    }
  };

  const loadQuota = async () => {
    try {
      const response = await axios.get('/api/ai-media/quota', { withCredentials: true });
      setQuota(response.data || { used: 0, limit: 0, remaining: 0 });
    } catch {
      // 配额加载失败不阻断主功能
    }
  };

  const loadProviderTokens = async () => {
    try {
      const response = await axios.get('/api/chat/my-tokens', { withCredentials: true });
      setProviderTokens(response.data?.tokens || []);
    } catch (err) {
      message.error(err.response?.data?.error || '加载 Token 失败');
    }
  };

  const openTokenModal = () => {
    setTokenInputs({ agnes: '', modelscope: '' });
    setTokenModalOpen(true);
    loadProviderTokens();
  };

  const handleSaveProviderToken = async (provider) => {
    const token = (tokenInputs[provider] || '').trim();
    if (!token) {
      message.warning('请先粘贴 Token');
      return;
    }
    setTokenSaving(provider);
    try {
      await axios.post('/api/chat/my-tokens', { provider, token }, { withCredentials: true });
      message.success('已保存');
      setTokenInputs((prev) => ({ ...prev, [provider]: '' }));
      await loadProviderTokens();
      // 保存后刷新渠道用量展示（免限额后剩余次数会随之变化）
      loadConfigs();
    } catch (err) {
      message.error(err.response?.data?.error || '保存失败');
    } finally {
      setTokenSaving('');
    }
  };

  const handleDeleteProviderToken = async (provider) => {
    try {
      await axios.delete(`/api/chat/my-tokens/${provider}`, { withCredentials: true });
      message.success('已清除');
      await loadProviderTokens();
      loadConfigs();
    } catch (err) {
      message.error(err.response?.data?.error || '清除失败');
    }
  };

  const handleCopyTokenGuide = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(TOKEN_GUIDE_URL);
      } else {
        const el = document.createElement('textarea');
        el.value = TOKEN_GUIDE_URL;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('链接已复制，请在浏览器中打开查看');
    } catch (err) {
      message.warning(`复制失败，请手动打开：${TOKEN_GUIDE_URL}`);
    }
  };

  const showHistory = () => {
    setTimeout(() => {
      const historyNode = document.querySelector('.mg-history');
      historyNode?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resetImageForm = () => {
    imageForm.resetFields();
    setImageIds([]);
    setImageUploads([]);
    setImageFileList([]);
    setImageResult(null);
  };

  const resetVideoForm = () => {
    videoForm.resetFields();
    setVideoImageIds([]);
    setVideoUploads([]);
    setVideoFileList([]);
    setVideoTask(null);
    setVideoTiming(getVideoTiming(videoForm.getFieldsValue()));
  };

  const showSubmittedModal = ({ type, record, onConfirm }) => {
    const isImage = type === 'image';
    const taskId = record?.taskId;
    Modal.confirm({
      title: isImage ? '图片任务已提交' : '视频任务已提交',
      content: (
        <div>
          <p>任务已进入生成历史，可以继续提交新任务，也可以去历史里刷新查看结果。</p>
          {taskId && <p>Task ID：<Text code>{taskId}</Text></p>}
        </div>
      ),
      okText: '确认',
      cancelText: '查看任务',
      maskClosable: true,
      onOk: onConfirm,
      onCancel: showHistory
    });
  };

  const configOptions = (capability) =>
    configs
      .filter((config) =>
        capability === 'image'
          ? config.supportsImageGeneration
          : config.supportsVideoGeneration
      )
      .map((config) => {
        const imageModels = Array.isArray(config.imageModels) ? config.imageModels : [];
        const videoModels = Array.isArray(config.videoModels) ? config.videoModels : [];
        const baseLabel = `${config.name} / ${
          capability === 'image'
            ? (imageModels.length > 0 ? `${imageModels.length} 个生图模型` : (config.imageModelName || 'image model'))
            : (videoModels.length > 0 ? `${videoModels.length} 个视频模型` : (config.videoModelName || 'video model'))
        }`;
        // 按 tab 用途取对应用量：image tab 用 imageUsed/imageLimit，video tab 用 videoUsed/videoLimit
        const used = capability === 'image' ? (config.imageUsed || 0) : (config.videoUsed || 0);
        const limit = capability === 'image' ? config.imageLimit : config.videoLimit;
        const hasLimit = limit && limit > 0;
        const remaining = hasLimit ? Math.max(0, limit - used) : null;
        const exhausted = hasLimit && remaining <= 0;
        return {
          value: config.id,
          disabled: exhausted,
          label: hasLimit
            ? `${baseLabel}（今日剩 ${remaining}/${limit} 次${exhausted ? '，已用完' : ''}）`
            : baseLabel,
        };
      });

  const dimensionsForPreset = (ratioValue, resolutionValue) => {
    const ratio = RATIO_PRESETS.find(item => item.value === ratioValue);
    const resolution = RESOLUTION_PRESETS.find(item => item.value === resolutionValue);
    if (!ratio || !resolution) return null;

    const landscape = ratio.width >= ratio.height;
    const scale = resolution.longEdge / (landscape ? ratio.width : ratio.height);
    return {
      width: Math.round(ratio.width * scale),
      height: Math.round(ratio.height * scale)
    };
  };

  const applySizePreset = (form, changedValues = {}) => {
    const values = {
      ...form.getFieldsValue(['sizeRatio', 'sizeResolution']),
      ...changedValues
    };
    const dimensions = dimensionsForPreset(values.sizeRatio, values.sizeResolution);
    if (!dimensions) return;
    form.setFieldsValue({
      width: dimensions.width,
      height: dimensions.height
    });
  };

  const sizeFromValues = (values) => {
    const width = Number(values.width);
    const height = Number(values.height);
    if (width && height) return `${width}x${height}`;
    // 兜底：用户从未展开「高级设置」或未触发 onValuesChange 时 width/height 为空，
    // 直接用当前 sizeRatio + sizeResolution 现算，保证宽高比一定随提交传给后端。
    const dimensions = dimensionsForPreset(values.sizeRatio, values.sizeResolution);
    if (dimensions && dimensions.width && dimensions.height) {
      return `${dimensions.width}x${dimensions.height}`;
    }
    return undefined;
  };

  const syncUploads = (field, fileList) => {
    const uploads = fileList.map((file) => file.response).filter((item) => item?.id);
    const ids = uploads.map((item) => item.id);
    if (field === 'image') {
      setImageIds(ids);
      setImageUploads(uploads);
    } else {
      setVideoImageIds(ids);
      setVideoUploads(uploads);
    }
  };

  const uploadProps = (field) => ({
    accept: 'image/*',
    action: '/api/chat/images',
    name: 'image',
    withCredentials: true,
    showUploadList: false,
    fileList: field === 'image' ? imageFileList : videoFileList,
    beforeUpload: () => true,
    onChange: (info) => {
      const list = info.fileList || [];
      if (field === 'image') setImageFileList(list);
      else setVideoFileList(list);
      syncUploads(field, list);
      if (info.file.status === 'done') {
        message.success(`参考图上传成功：${info.file.name}`);
      } else if (info.file.status === 'error') {
        message.error(info.file.response?.error || `参考图上传失败：${info.file.name}`);
      }
    },
  });

  const removeUpload = (field, uid) => {
    const list = (field === 'image' ? imageFileList : videoFileList).filter((f) => f.uid !== uid);
    if (field === 'image') setImageFileList(list);
    else setVideoFileList(list);
    syncUploads(field, list);
  };

  const renderUploadGrid = (field, addLabel) => {
    const list = field === 'image' ? imageFileList : videoFileList;
    return (
      <div className="mg-upload-grid">
        <div className="mg-upload-cards">
          {list.map((file) => {
            const url = file.response?.temporaryUrl;
            return (
              <div
                key={file.uid}
                className={`mg-upload-card${file.status === 'error' ? ' error' : ''}`}
              >
                {file.status === 'done' && url ? (
                  <img src={url} alt={file.name} onClick={() => openImagePreview(url)} />
                ) : (
                  <span className="mg-upload-card-loading">
                    <LoadingOutlined />
                  </span>
                )}
                <button
                  type="button"
                  className="mg-upload-remove"
                  onClick={() => removeUpload(field, file.uid)}
                >
                  <CloseOutlined />
                </button>
              </div>
            );
          })}
          <Upload {...uploadProps(field)}>
            <div className="mg-upload-add">
              <CloudUploadOutlined className="mg-upload-add-icon" />
              <span className="mg-upload-add-text">{addLabel}</span>
            </div>
          </Upload>
        </div>
        <div className="mg-upload-tip">支持 png / jpg / webp，可多选，单张不超过 10M</div>
      </div>
    );
  };

  const copyText = async (text, label = '内容') => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label}已复制`);
    } catch (err) {
      message.error(err.message || '复制失败');
    }
  };

  async function downloadFile(url, defaultType) {
    if (!url) return;

    const typeLabel = defaultType === 'image' ? '图片' : '视频';
    if (sendDownloadToAndroid(url, defaultType)) {
      message.success('Download started');
      return;
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);

    let ext = '';
    const urlExt = url.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
    if (urlExt) {
      ext = urlExt[1].toLowerCase();
    }

    let filename = `ai-${defaultType}-${timestamp}.${ext || (defaultType === 'image' ? 'png' : 'mp4')}`;

    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      if (!ext) {
        const mimeExtMap = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
          'image/gif': 'gif',
          'video/mp4': 'mp4',
          'video/webm': 'webm',
          'video/quicktime': 'mov',
        };
        const mimeType = (blob.type || '').split(';')[0].toLowerCase();
        ext = mimeExtMap[mimeType] || (defaultType === 'image' ? 'png' : 'mp4');
        filename = `ai-${defaultType}-${timestamp}.${ext}`;
      }

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        a.remove();
      }, 100);

      message.success(`${typeLabel}下载中`);
    } catch (err) {
      console.warn(`下载失败，已在新标签页打开`, err);
      window.open(url, '_blank');
      message.info(`${typeLabel}已在新标签页打开，请自行保存`);
    }
  }

  const generateImage = async () => {
    try {
      const values = await imageForm.validateFields();
      // 「渠道下挂多个生图模型」：优先用用户选的 imageModel，回退渠道默认 imageModelName。
      const cfg = configs.find((c) => String(c.id) === String(values.configId));
      const cfgImageModels = Array.isArray(cfg?.imageModels) ? cfg.imageModels : [];
      const chosenModel = values.imageModel || cfg?.imageModelName || undefined;
      // 必传图校验（仅前端）：选中模型标记 requireImage 时，必须先上传参考图。
      const chosenDef = cfgImageModels.find((m) => m.model === values.imageModel);
      if (chosenDef?.requireImage === true && imageIds.length === 0) {
        message.error('该模型为图片编辑模型，请先上传待编辑图片');
        return;
      }
      setImageSubmitting(true);
      setImageResult(null);
      // Agnes 生图模型：按官方文档推荐「档位式 size（1K/2K/4K）+ ratio」，输出尺寸可预期；
      // 非原生的精确 WxH 会被上游标准化（可能降档）。用户在高级设置手改过宽高时仍按精确尺寸提交。
      let size = sizeFromValues(values);
      let ratio;
      const isAgnesImage = /^agnes-image/i.test(String(chosenModel || '')) || cfg?.interfaceType === 'agnes';
      if (isAgnesImage) {
        const presetDims = dimensionsForPreset(values.sizeRatio, values.sizeResolution);
        const manuallyOverridden = values.width && values.height && presetDims
          && (Number(values.width) !== presetDims.width || Number(values.height) !== presetDims.height);
        const tier = RESOLUTION_PRESETS.find(item => item.value === values.sizeResolution);
        if (!manuallyOverridden && tier) {
          size = tier.label;
          ratio = values.sizeRatio;
        }
      }
      const response = await axios.post(
        '/api/ai-media/images',
        {
          ...values,
          model: chosenModel,
          size,
          ...(ratio && { ratio }),
          imageIds,
          async: true
        },
        { withCredentials: true }
      );
      loadQuota();
      await loadRecords();
      showSubmittedModal({
        type: 'image',
        record: response.data.record,
        onConfirm: resetImageForm
      });
    } catch (err) {
      if (err.response?.status === 429) {
        const data = err.response.data || {};
        message.error({ content: data.error || '今日生成次数已达上限，请联系管理员', duration: 5 });
      } else {
        message.error(err.response?.data?.error || err.message || '图片生成失败');
      }
    } finally {
      setImageSubmitting(false);
    }
  };

  const createVideo = async () => {
    try {
      const values = await videoForm.validateFields();
      // v2.0：ti2vid 模式（含未选模式的默认情况）上游仅支持 1 张图；多图需显式选 keyframes 模式。
      if (!isVideo25 && values.mode !== 'keyframes' && videoImageIds.length > 1) {
        message.error('ti2vid 模式仅支持 1 张参考图，若需多图请将「多图模式」选为 keyframes');
        return;
      }
      // 「渠道下挂多个视频模型」：优先用用户选的 videoModel，回退渠道默认 videoModelName。
      const cfg = configs.find((c) => String(c.id) === String(values.configId));
      const cfgVideoModels = Array.isArray(cfg?.videoModels) ? cfg.videoModels : [];
      const chosenModel = values.videoModel || cfg?.videoModelName || undefined;
      // 必传图校验（仅前端）：选中模型标记 requireImage 时（图生视频），必须先上传参考图。
      const chosenDef = cfgVideoModels.find((m) => m.model === values.videoModel);
      if (chosenDef?.requireImage === true && videoImageIds.length === 0) {
        message.error('该模型为图生视频模型，请先上传参考图');
        return;
      }
      let requestBody;
      if (isVideo25) {
        // agnes-video-2.5 族走新参数接口：画幅白名单前端先卡一道；flash 参考图 ≤5、固定 720P。
        if (!VIDEO25_RATIO_WHITELIST.includes(values.sizeRatio)) {
          message.error('该模型的画幅比例仅支持 21:9 / 16:9 / 4:3 / 1:1 / 3:4 / 9:16');
          return;
        }
        if (isVideoFlash && videoImageIds.length > VIDEO25_MAX_REFERENCE_IMAGES) {
          message.error('agnes-video-2.5-flash 参考图最多 5 张');
          return;
        }
        requestBody = {
          configId: values.configId,
          prompt: values.prompt,
          model: chosenModel,
          aspectRatio: values.sizeRatio,
          size: isVideoFlash ? '720P' : (values.sizeResolution || undefined),
          durationSeconds: Number(values.durationSeconds),
          ...(values.seed !== undefined && values.seed !== null && { seed: values.seed }),
          imageIds: videoImageIds
        };
      } else {
        const timing = getVideoTiming(values);
        const payloadValues = { ...values };
        delete payloadValues.durationSeconds;
        requestBody = { ...payloadValues, model: chosenModel, numFrames: timing.numFrames, imageIds: videoImageIds };
      }
      setVideoSubmitting(true);
      setVideoTask(null);
      const response = await axios.post(
        '/api/ai-media/videos',
        requestBody,
        { withCredentials: true }
      );
      loadQuota();
      setVideoTask({ ...response.data, configId: values.configId });
      await loadRecords();
      showSubmittedModal({
        type: 'video',
        record: response.data.record,
        onConfirm: resetVideoForm
      });
    } catch (err) {
      if (err.response?.status === 429) {
        const data = err.response.data || {};
        message.error({ content: data.error || '今日生成次数已达上限，请联系管理员', duration: 5 });
      } else {
        message.error(err.response?.data?.error || err.message || '视频任务创建失败');
      }
    } finally {
      setVideoSubmitting(false);
    }
  };

  const refreshVideo = async () => {
    const taskId = videoTask?.taskId || videoTask?.id;
    if (!taskId || !videoTask?.configId) return;
    setPolling(true);
    try {
      const response = await axios.get(`/api/ai-media/videos/${taskId}`, {
        params: { configId: videoTask.configId },
        withCredentials: true,
      });
      setVideoTask({ ...response.data, configId: videoTask.configId });
      await loadRecords();
    } catch (err) {
      message.error(err.response?.data?.error || '视频任务刷新失败');
    } finally {
      setPolling(false);
    }
  };

  const refreshVideoRecord = async (record) => {
    if (!record.taskId || !record.configId) return;
    setRefreshingRecordId(record.id);
    try {
      const response = await axios.get(`/api/ai-media/videos/${record.taskId}`, {
        params: { configId: record.configId },
        withCredentials: true,
      });
      setVideoTask({ ...response.data, configId: record.configId });
      await loadRecords();
      message.success('视频进度已刷新');
    } catch (err) {
      message.error(err.response?.data?.error || '视频任务刷新失败');
    } finally {
      setRefreshingRecordId(null);
    }
  };

  // 图像任务刷新：用 record.taskId 去上游复查（含后台已超时仍 pending 的记录），成功则救回结果。
  const refreshImageRecord = async (record) => {
    if (!record.taskId || !record.configId) return;
    setRefreshingRecordId(record.id);
    try {
      await axios.get(`/api/ai-media/images/${record.taskId}`, {
        params: { configId: record.configId },
        withCredentials: true,
      });
      await loadRecords();
      message.success('图片进度已刷新');
    } catch (err) {
      message.error(err.response?.data?.error || '图片任务刷新失败');
    } finally {
      setRefreshingRecordId(null);
    }
  };

  const videoPreviewSrc = (source) => {
    const recordId = source?.record?.id || source?.id;
    return recordId ? `/api/ai-media/records/${recordId}/video` : (source?.videoUrl || source?.resultUrl || '');
  };

  const openImagePreview = (src) => {
    if (!src) return;
    setImagePreview(src);
    if (!imagePreviewHistoryRef.current && window.history?.pushState) {
      window.history.pushState({ aiMediaImagePreview: true }, '');
      imagePreviewHistoryRef.current = true;
    }
  };

  const closeImagePreview = () => {
    setImagePreview(null);
    if (imagePreviewHistoryRef.current && window.history?.back) {
      imagePreviewHistoryRef.current = false;
      window.history.back();
    }
  };

  const deleteRecord = async (record) => {
    try {
      await axios.delete(`/api/ai-media/records/${record.id}`, { withCredentials: true });
      setRecords(records.filter(item => item.id !== record.id));
      message.success('历史记录已删除');
    } catch (err) {
      message.error(err.response?.data?.error || '删除历史记录失败');
    }
  };

  const renderTemporaryUrls = (uploads) => {
    const urls = uploads.map((item) => item.temporaryUrl).filter(Boolean);
    if (urls.length === 0) return null;

    return (
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 8 }}
        message="已生成临时图片 URL"
        description={(
          <Space direction="vertical" style={{ width: '100%' }}>
            {urls.map((url, index) => (
              <Space key={url} wrap>
                <a href={url} target="_blank" rel="noopener noreferrer">参考图 {index + 1}</a>
                <Button size="small" onClick={() => copyText(url, '临时图片 URL')}>复制 URL</Button>
              </Space>
            ))}
          </Space>
        )}
      />
    );
  };

  const renderImageResult = () => {
    const images = imageResult?.images || [];
    if (images.length === 0) return null;

    return (
      <div className="mg-result-card">
        <div className="mg-result-title">生成结果</div>
        <div className="mg-result-grid">
          {images.map((image, index) => {
            const src = imageSrc(image);
            const markdown = src ? `![generated-image](${src})` : '';
            return (
              <Card key={index} size="small" bodyStyle={{ padding: 10 }}>
                {src && (
                  <img
                    src={src}
                    alt="generated"
                    className="mg-result-image"
                    onClick={() => openImagePreview(src)}
                  />
                )}
                <Space wrap className="mg-result-actions">
                  {src && <Button size="small" onClick={() => downloadFile(src, 'image')}>下载</Button>}
                  <Button size="small" onClick={() => copyText(src, '图片地址')}>复制地址</Button>
                  <Button size="small" onClick={() => copyText(markdown, 'Markdown')}>复制 Markdown</Button>
                </Space>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  const renderVideoResult = () => {
    if (!videoTask) return null;
    const videoHtml = videoTask.videoUrl ? `<video controls src="${videoTask.videoUrl}"></video>` : '';
    const taskId = videoTask.taskId || videoTask.id;
    const errorMessage = videoErrorMessage(videoTask);
    const statusText = videoTask.status || 'unknown';
    const statusColor =
      String(statusText).toLowerCase() === 'succeeded' || videoTask.videoUrl ? 'success'
        : errorMessage ? 'error'
        : 'processing';

    return (
      <div className="mg-result-card mg-video-card">
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
          <span className="mg-result-title">视频任务</span>
          <Tag color={statusColor} className="mg-status-tag">{statusText}</Tag>
        </Space>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">Task ID：</Text>
          <Text code>{taskId || '等待返回'}</Text>
        </div>
        {videoTask.progress !== undefined && <Progress percent={Number(videoTask.progress) || 0} />}
        {errorMessage && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12, marginTop: 8 }}
            message="视频生成失败"
            description={errorMessage}
          />
        )}
        {videoTask.videoUrl && (
          <>
            <video controls src={videoPreviewSrc(videoTask)} />
            <Space wrap className="mg-result-actions">
              <Button size="small" onClick={() => downloadFile(videoTask.videoUrl, 'video')}>下载</Button>
              <Button size="small" onClick={() => copyText(videoTask.videoUrl, '视频地址')}>复制地址</Button>
              <Button size="small" onClick={() => copyText(videoHtml, 'HTML')}>复制 HTML</Button>
            </Space>
          </>
        )}
        <Divider style={{ margin: '12px 0 8px' }} />
        <Button loading={polling} disabled={!taskId} onClick={refreshVideo}>刷新进度</Button>
      </div>
    );
  };

  // 历史回显：用 record.configId 在已加载的 configs 里查出用户当时选的渠道名。
  // 渠道可能已被删除/停用（configs 里查不到），此时回退为「渠道#ID」，避免历史显示空白。
  const getConfigName = (configId) => {
    if (configId === undefined || configId === null) return '未知渠道';
    const target = configs.find((item) => String(item.id) === String(configId));
    return target ? target.name : `渠道#${configId}`;
  };

  const renderHistoryPreview = (record) => {
    if (record.type === 'image' && record.resultUrl) {
      return (
        <img
          src={record.resultUrl}
          alt="generated"
          className="media-history-image"
          style={{ cursor: 'zoom-in' }}
          onClick={() => openImagePreview(record.resultUrl)}
        />
      );
    }
    const errorMessage = videoErrorMessage(record);
    if (errorMessage) {
      return <Text type="danger">{errorMessage}</Text>;
    }
    if (!record.resultUrl) {
      return <Text type="secondary">暂无结果链接</Text>;
    }
    return (
      <video controls src={videoPreviewSrc(record)} className="media-history-video" />
    );
  };

  const renderHistory = () => (
    <div className="mg-history media-history">
      <div className="mg-history-head">
        <span className="mg-history-title">生成历史</span>
        <Button size="small" onClick={loadRecords} loading={recordsLoading}>刷新</Button>
      </div>
      {records.length === 0 ? (
        <Text type="secondary">暂无生成历史</Text>
      ) : (
        <div className="media-history-grid">
          {records.map(record => {
            const lowerStatus = String(record.status || '').toLowerCase();
            const isTerminal = !!record.resultUrl
              || lowerStatus === 'succeeded'
              || lowerStatus === 'success'
              || lowerStatus === 'failed';
            const historyStatusColor =
              (record.resultUrl && (lowerStatus === 'succeeded' || lowerStatus === 'success')) ? 'success'
                : lowerStatus === 'failed' || videoErrorMessage(record) ? 'error'
                : (lowerStatus === 'queued' || lowerStatus === 'pending' || lowerStatus === 'processing' || lowerStatus === 'running' || lowerStatus === 'in_progress') ? 'processing'
                : 'default';
            const showRefresh = !!record.taskId && !isTerminal;
            return (
              <Card key={record.id} size="small" bodyStyle={{ padding: 12 }} className="media-history-card">
                <div className="mg-hist-tags">
                  <Tag color={record.type === 'image' ? 'blue' : 'purple'}>
                    {record.type === 'image' ? '图片' : '视频'}
                  </Tag>
                  <Tag color={historyStatusColor} className="mg-status-tag">{record.status || 'unknown'}</Tag>
                  <Tag color="geekblue">{getConfigName(record.configId)}</Tag>
                  <Popconfirm
                    title="删除这条历史记录？"
                    okText="删除"
                    cancelText="取消"
                    onConfirm={() => deleteRecord(record)}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} className="mg-hist-del" />
                  </Popconfirm>
                </div>
                <Paragraph ellipsis={{ rows: 2 }} className="mg-hist-prompt">
                  {record.prompt}
                </Paragraph>
                <div className="mg-hist-preview">
                  {renderHistoryPreview(record)}
                </div>
                <div className="media-history-meta">
                  <Text type="secondary">创建：{new Date(record.createdAt).toLocaleString()}</Text>
                  <Text type="secondary">过期：{new Date(record.expiresAt).toLocaleString()}</Text>
                </div>
                {record.taskId && (
                  <div className="media-history-task">
                    <Text type="secondary">Task ID：</Text>
                    <Text copyable className="mg-hist-taskid">{record.taskId}</Text>
                  </div>
                )}
                {(record.resultUrl || showRefresh) && (
                  <Space wrap size={[6, 6]} className="mg-result-actions">
                    {record.resultUrl && (
                      <Button size="small" onClick={() => downloadFile(record.resultUrl, record.type)}>下载</Button>
                    )}
                    {showRefresh && record.type === 'video' && (
                      <Button
                        size="small"
                        loading={refreshingRecordId === record.id}
                        onClick={() => refreshVideoRecord(record)}
                      >
                        刷新进度
                      </Button>
                    )}
                    {record.type === 'image' && record.taskId && (
                      <Button
                        size="small"
                        loading={refreshingRecordId === record.id}
                        onClick={() => refreshImageRecord(record)}
                      >
                        刷新进度
                      </Button>
                    )}
                  </Space>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const imageOptions = configOptions('image');
  const videoOptions = configOptions('video');

  // 观察图片表单当前选中的渠道与模型，用于「渠道下挂多个生图模型」的二级选择器与必传图校验。
  const watchedImageConfigId = Form.useWatch('configId', imageForm);
  const watchedImageModel = Form.useWatch('imageModel', imageForm);
  const selectedImageConfig = configs.find((c) => String(c.id) === String(watchedImageConfigId));
  const selectedImageModels = Array.isArray(selectedImageConfig?.imageModels)
    ? selectedImageConfig.imageModels
    : [];
  const imageModelOptions = selectedImageModels.map((m) => {
    const CAP_LABEL = { generate: '只生图', edit: '只编辑', both: '生图/编辑' };
    return {
      value: m.model,
      label: `${m.model}（${CAP_LABEL[m.capability] || m.capability}${m.requireImage ? '·需上传图' : ''}）`,
    };
  });
  // 当前选中的生图模型项（用于必传图校验）；未配 imageModels 时为 undefined，回退旧行为。
  const currentImageModelDef = selectedImageModels.find((m) => m.model === watchedImageModel);
  const currentImageRequireImage = currentImageModelDef?.requireImage === true;

  // 观察视频表单当前选中的渠道与模型，用于「渠道下挂多个视频模型」的二级选择器与必传图校验。
  const watchedVideoConfigId = Form.useWatch('configId', videoForm);
  const watchedVideoModel = Form.useWatch('videoModel', videoForm);
  const selectedVideoConfig = configs.find((c) => String(c.id) === String(watchedVideoConfigId));
  const selectedVideoModels = Array.isArray(selectedVideoConfig?.videoModels)
    ? selectedVideoConfig.videoModels
    : [];
  const videoModelOptions = selectedVideoModels.map((m) => ({
    value: m.model,
    label: `${m.model}${m.requireImage ? '（需上传参考图）' : ''}`,
  }));
  // 当前选中的视频模型项（用于必传图校验）；未配 videoModels 时为 undefined，回退旧行为。
  const currentVideoModelDef = selectedVideoModels.find((m) => m.model === watchedVideoModel);
  const currentVideoRequireImage = currentVideoModelDef?.requireImage === true;

  // 视频模型属于 agnes-video-2.5 族（含 flash）时，表单整体切换为新参数接口形态：
  // 画幅比例/清晰度档位/时长秒数取代 宽高+帧数+FPS；参考图走公网 URL。
  const effectiveVideoModelName = watchedVideoModel || selectedVideoConfig?.videoModelName || '';
  const isVideo25 = VIDEO_MODEL_25_RE.test(effectiveVideoModelName);
  const isVideoFlash = VIDEO_MODEL_FLASH_RE.test(effectiveVideoModelName);
  const ratioPresetsForForm = isVideo25
    ? RATIO_PRESETS.filter((p) => VIDEO25_RATIO_WHITELIST.includes(p.value))
    : RATIO_PRESETS;
  const sizeOptionsForForm = isVideoFlash ? VIDEO25_FLASH_SIZE_OPTIONS : VIDEO25_SIZE_OPTIONS;

  return (
    <>
    <Modal
      visible={!!imagePreview}
      footer={null}
      onCancel={closeImagePreview}
      width="90vw"
      bodyStyle={{ padding: 0, textAlign: 'center', background: '#111' }}
      destroyOnClose
    >
      {imagePreview && (
        <img
          src={imagePreview}
          alt="preview"
          style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
        />
      )}
    </Modal>
    <div className="mg-studio">
      {quota.limit > 0 && quota.remaining <= 0 && (
        <Alert
          type="error"
          showIcon
          message="今日生成次数已达上限"
          description="请联系管理员重置配额或等待明日自动恢复"
        />
      )}
      <div className="mg-card">
        {configs.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="暂无启用的图片/视频生成配置"
            description="请先到后台 AI配置管理 创建 Agnes 或 StepFun 配置，并打开图片生成或视频生成能力。"
          />
        ) : (
          <>
            <div className="mg-hero">
              <div className="mg-hero-main">
                <span className="mg-hero-badge"><ThunderboltOutlined /></span>
                <div className="mg-hero-text">
                  <div className="mg-hero-title">AI 创作工作室</div>
                  <div className="mg-hero-sub">用文字生成图片与视频 · 多模型渠道随心切换</div>
                </div>
              </div>
              <div className="mg-hero-side">
                <Button
                  size="small"
                  icon={<UnlockOutlined />}
                  onClick={openTokenModal}
                >
                  解除次数限制
                </Button>
                <Segmented
                  value={activeMediaTab}
                  onChange={handleMediaTabChange}
                  options={[
                    { label: <span><PictureOutlined /> 图片</span>, value: 'image' },
                    { label: <span><VideoCameraOutlined /> 视频</span>, value: 'video' },
                  ]}
                />
                {quota.limit > 0 && (
                  <span className={`mg-quota ${quota.remaining <= 0 ? 'danger' : quota.remaining <= 2 ? 'warning' : 'secondary'}`}>
                    <i className="mg-quota-dot" />今日剩余 {quota.remaining}/{quota.limit} 次
                  </span>
                )}
              </div>
            </div>

            {activeMediaTab === 'image' ? (
              <>
              <Form
                key="image-form"
                form={imageForm}
                layout="vertical"
                initialValues={{
                  sizeRatio: '1:1',
                  sizeResolution: '2k',
                  width: 2048,
                  height: 2048,
                  imageInputFormat: 'base64',
                  imageResultFormat: 'base64'
                }}
                onValuesChange={(changed) => {
                  if (changed.sizeRatio || changed.sizeResolution) applySizePreset(imageForm, changed);
                }}
              >
                <div className="mg-params">
                  <Form.Item name="configId" label="AI 渠道" rules={[{ required: true, message: '请选择 AI 配置' }]}>
                    <Select
                      options={imageOptions}
                      dropdownClassName="mg-dropdown"
                      placeholder="选择生图渠道"
                      onChange={() => imageForm.setFieldsValue({ imageModel: undefined })}
                    />
                  </Form.Item>
                  {selectedImageModels.length > 0 && (
                    <Form.Item
                      name="imageModel"
                      label="生图模型"
                      rules={[{ required: true, message: '请选择生图模型' }]}
                      extra={currentImageRequireImage ? '该模型为图片编辑模型，需先上传待编辑图片。' : undefined}
                    >
                      <Select options={imageModelOptions} dropdownClassName="mg-dropdown" placeholder="请选择该渠道下的生图模型" />
                    </Form.Item>
                  )}
                </div>
                <div className="mg-prompt-card">
                  <div className="mg-prompt-label"><HighlightOutlined /> 提示词</div>
                  <Form.Item name="prompt" rules={[{ required: true, message: '请输入提示词' }]} noStyle>
                    <TextArea
                      className="mg-prompt-textarea"
                      autoSize={{ minRows: 4, maxRows: 10 }}
                      placeholder="描述你想要的画面，越具体越好…"
                    />
                  </Form.Item>
                </div>
                <Form.Item name="sizeRatio" label="画面比例" className="mg-ratio-item">
                  <RatioSelect />
                </Form.Item>
                <div className="mg-params">
                  <Form.Item name="sizeResolution" label="清晰度">
                    <Segmented block options={RESOLUTION_PRESETS.map(({ value, label }) => ({ value, label }))} />
                  </Form.Item>
                </div>
                <div className="mg-advanced">
                  <Collapse ghost expandIconPosition="right">
                    <Panel header={<span><SettingOutlined /> 高级设置</span>} key="image-advanced">
                      <Space wrap>
                        <Form.Item name="width" label="宽" rules={[{ required: true, message: '请输入宽度' }]}>
                          <InputNumber min={64} max={3840} precision={0} />
                        </Form.Item>
                        <Form.Item name="height" label="高" rules={[{ required: true, message: '请输入高度' }]}>
                          <InputNumber min={64} max={3840} precision={0} />
                        </Form.Item>
                      </Space>
                      <Form.Item name="imageInputFormat" label="参考图传给模型的方式">
                        <Radio.Group>
                          <Radio.Button value="base64">Base64</Radio.Button>
                          <Radio.Button value="url">图片 URL</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item name="imageResultFormat" label="生成结果返回方式">
                        <Radio.Group>
                          <Radio.Button value="base64">Base64</Radio.Button>
                          <Radio.Button value="url">图片 URL</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                    </Panel>
                  </Collapse>
                </div>
                <div className="mg-upload">
                  {renderUploadGrid('image', '上传图片')}
                  {renderTemporaryUrls(imageUploads)}
                </div>
                <div className="mg-actions">
                  <Button
                    type="primary"
                    className="mg-primary-btn"
                    size="large"
                    loading={imageSubmitting}
                    onClick={generateImage}
                    disabled={quota.limit > 0 && quota.remaining <= 0}
                  >
                    <ThunderboltOutlined /> 立即生成图片
                  </Button>
                </div>
              </Form>
              {renderImageResult()}
              </>
            ) : (
              <>
              <Form
                key="video-form"
                form={videoForm}
                layout="vertical"
                initialValues={{
                  sizeRatio: '16:9',
                  sizeResolution: '4k',
                  width: DEFAULT_VIDEO_WIDTH,
                  height: DEFAULT_VIDEO_HEIGHT,
                  durationSeconds: DEFAULT_VIDEO_DURATION_SECONDS,
                  frameRate: DEFAULT_VIDEO_FRAME_RATE,
                  imageInputFormat: 'base64',
                }}
                onValuesChange={(changed, allValues) => {
                  if (changed.sizeRatio || changed.sizeResolution) applySizePreset(videoForm, changed);
                  setVideoTiming(getVideoTiming(allValues));
                }}
              >
                <div className="mg-params">
                  <Form.Item name="configId" label="AI 渠道" rules={[{ required: true, message: '请选择 AI 配置' }]}>
                    <Select
                      options={videoOptions}
                      dropdownClassName="mg-dropdown"
                      placeholder="选择视频渠道"
                      onChange={() => videoForm.setFieldsValue({ videoModel: undefined })}
                    />
                  </Form.Item>
                  {selectedVideoModels.length > 0 && (
                    <Form.Item
                      name="videoModel"
                      label="视频模型"
                      rules={[{ required: true, message: '请选择视频模型' }]}
                      extra={currentVideoRequireImage ? '该模型为图生视频模型，需先上传参考图。' : undefined}
                    >
                      <Select options={videoModelOptions} dropdownClassName="mg-dropdown" placeholder="请选择该渠道下的视频模型" />
                    </Form.Item>
                  )}
                </div>
                <div className="mg-prompt-card">
                  <div className="mg-prompt-label"><HighlightOutlined /> 提示词</div>
                  <Form.Item name="prompt" rules={[{ required: true, message: '请输入提示词' }]} noStyle>
                    <TextArea
                      className="mg-prompt-textarea"
                      autoSize={{ minRows: 4, maxRows: 10 }}
                      placeholder="描述你想要的视频画面…"
                    />
                  </Form.Item>
                </div>
                <Form.Item name="sizeRatio" label={isVideo25 ? '画幅比例' : '画面比例'} className="mg-ratio-item">
                  <RatioSelect presets={ratioPresetsForForm} />
                </Form.Item>
                <div className="mg-params">
                  <Form.Item name="sizeResolution" label={isVideo25 ? '清晰度档位' : '清晰度'}>
                    {isVideo25 ? (
                      <Segmented block options={sizeOptionsForForm} />
                    ) : (
                      <Segmented block options={RESOLUTION_PRESETS.map(({ value, label }) => ({ value, label }))} />
                    )}
                  </Form.Item>
                  <Form.Item
                    name="durationSeconds"
                    label="时长"
                    rules={[{ required: true, message: isVideo25 ? '请选择视频时长（4-12 秒）' : '请选择视频时长' }]}
                  >
                    <Select options={isVideo25 ? VIDEO25_DURATION_OPTIONS : VIDEO_DURATION_OPTIONS} dropdownClassName="mg-dropdown" />
                  </Form.Item>
                  {!isVideo25 && (
                    <Form.Item name="frameRate" label="FPS" rules={[{ required: true, message: '请输入 FPS' }]}>
                      <InputNumber min={MIN_VIDEO_FRAME_RATE} max={MAX_VIDEO_FRAME_RATE} precision={0} />
                    </Form.Item>
                  )}
                </div>
                {!isVideo25 && videoTiming.numFrames && (
                  <Alert
                    type={videoTiming.isCapped ? 'warning' : 'info'}
                    showIcon
                    style={{ marginBottom: 12, marginTop: 4 }}
                    message={`目标时长：约 ${videoTiming.durationSeconds} 秒，提交帧数：${videoTiming.numFrames} 帧（${videoTiming.frameRate} FPS，实际约 ${videoTiming.actualSeconds} 秒）`}
                    description={videoTiming.isCapped ? `已按当前分辨率上限提交 ${videoTiming.maxFrames} 帧；实际秒数由 FPS 决定。` : null}
                  />
                )}
                <div className="mg-advanced">
                  <Collapse ghost expandIconPosition="right" defaultActiveKey={[]}>
                    <Panel header={<span><SettingOutlined /> 高级设置</span>} key="video-advanced">
                      {!isVideo25 && (
                        <>
                          <Space wrap>
                            <Form.Item name="width" label="宽" rules={[{ required: true, message: '请输入宽度' }]}>
                              <InputNumber min={64} max={3840} precision={0} />
                            </Form.Item>
                            <Form.Item name="height" label="高" rules={[{ required: true, message: '请输入高度' }]}>
                              <InputNumber min={64} max={3840} precision={0} />
                            </Form.Item>
                          </Space>
                          <Form.Item name="negativePrompt" label="反向提示词"><Input /></Form.Item>
                          <Form.Item
                            name="mode"
                            label="多图模式"
                            tooltip="ti2vid：文/图生视频，仅支持 1 张参考图；keyframes：关键帧模式，支持多张图。上传多图时请选 keyframes。"
                          >
                            <Select
                              allowClear
                              dropdownClassName="mg-dropdown"
                              placeholder="默认 ti2vid（单图）"
                              options={[
                                { value: 'ti2vid', label: 'ti2vid（单图）' },
                                { value: 'keyframes', label: 'keyframes（多图）' },
                              ]}
                            />
                          </Form.Item>
                          <Form.Item name="imageInputFormat" label="参考图传给 Agnes 的方式">
                            <Radio.Group>
                              <Radio.Button value="base64">Base64</Radio.Button>
                              <Radio.Button value="url">图片 URL</Radio.Button>
                            </Radio.Group>
                          </Form.Item>
                        </>
                      )}
                      {isVideo25 && (
                        <>
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="agnes-video-2.5 族参数说明"
                            description="时长 4-12 秒；清晰度档位与画幅比例由模型白名单限定；flash 模型固定 720P 且参考图最多 5 张；参考图将以公开 URL 提交给上游。"
                          />
                          <Form.Item name="seed" label="随机种子（可选）" tooltip="传入相同种子可提高结果可复现性">
                            <InputNumber min={0} precision={0} style={{ width: 220 }} placeholder="留空随机" />
                          </Form.Item>
                        </>
                      )}
                    </Panel>
                  </Collapse>
                </div>
                <div className="mg-upload">
                  {renderUploadGrid('video', isVideo25 ? '上传参考图（0 张=纯文生视频，flash 最多 5 张）' : '上传参考图')}
                  {renderTemporaryUrls(videoUploads)}
                </div>
                <div className="mg-actions">
                  <Button
                    type="primary"
                    className="mg-primary-btn"
                    size="large"
                    loading={videoSubmitting}
                    onClick={createVideo}
                    disabled={quota.limit > 0 && quota.remaining <= 0}
                  >
                    <VideoCameraOutlined /> 创建视频任务
                  </Button>
                </div>
              </Form>
              {renderVideoResult()}
              </>
            )}
          </>
        )}
      </div>
      {renderHistory()}
    </div>
    <Modal
      title={<span><KeyOutlined /> 解除次数限制</span>}
      open={tokenModalOpen}
      onCancel={() => setTokenModalOpen(false)}
      footer={null}
      destroyOnClose
    >
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        上传你自己的 ModelScope / Agnes Token 后，用对应渠道生图、生视频将不再受平台每日次数限制，并改用你自己的 Token 调用。Token 加密存储。
      </Paragraph>
      <Button
        icon={<QuestionCircleOutlined />}
        onClick={handleCopyTokenGuide}
        block
        style={{ marginBottom: 16 }}
      >
        如何获取 Token
      </Button>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {PROVIDER_META.map((provider) => {
          const record = providerTokens.find((item) => item.provider === provider.key);
          const configured = record?.hasToken;
          return (
            <div key={provider.key}>
              <Space style={{ marginBottom: 8 }}>
                <Text strong>{provider.label}</Text>
                {configured
                  ? <Tag color="green">已配置 {record.tokenPreview ? `(${record.tokenPreview})` : ''}</Tag>
                  : <Tag>未配置</Tag>}
              </Space>
              <Space.Compact style={{ width: '100%' }}>
                <Input.Password
                  placeholder={provider.placeholder}
                  value={tokenInputs[provider.key]}
                  onChange={(e) => setTokenInputs((prev) => ({ ...prev, [provider.key]: e.target.value }))}
                />
                <Button
                  type="primary"
                  loading={tokenSaving === provider.key}
                  onClick={() => handleSaveProviderToken(provider.key)}
                >
                  保存
                </Button>
              </Space.Compact>
              {configured && (
                <Popconfirm
                  title={`清除 ${provider.label} Token？清除后该渠道恢复次数限制。`}
                  okText="清除"
                  cancelText="取消"
                  onConfirm={() => handleDeleteProviderToken(provider.key)}
                >
                  <Button type="link" danger size="small" style={{ paddingLeft: 0, marginTop: 4 }}>
                    清除此 Token
                  </Button>
                </Popconfirm>
              )}
            </div>
          );
        })}
      </Space>
    </Modal>
    </>
  );
}

export default forwardRef(MediaGenerator);
